import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UploadedDateFilter } from '~/components/Filters/DateFilter/UploadedDateFilter';
import { type BaseFilterCardProps, FilterCard } from '~/components/Filters/FilterCard';
import {
  removeFilterAction,
  setUploadedDateFilterEndDateAction,
  setUploadedDateFilterStartDateAction,
  setUploadedDateFilterTypeAction,
} from '~/store/filters/actions';
import {
  uploadedDateFilterDateTypeSelector,
  uploadedDateFilterEndDateSelector,
  uploadedDateFilterStartDateSelector,
} from '~/store/filters/selectors';
import { type DateRangeType, type SelectedFilter } from '~/store/filters/types';

export type UploadedDateFilterCardProps = BaseFilterCardProps;

export const UploadedDateFilterCard = memo((props: UploadedDateFilterCardProps) => {
  const dispatch = useDispatch();
  const dateType = useSelector(uploadedDateFilterDateTypeSelector);
  const startDate = useSelector(uploadedDateFilterStartDateSelector);
  const endDate = useSelector(uploadedDateFilterEndDateSelector);

  const onDateTypeChange = useCallback(
    (type: DateRangeType) => {
      dispatch(setUploadedDateFilterTypeAction({ type }));
    },
    [dispatch],
  );

  const onStartDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setUploadedDateFilterStartDateAction({ startDate: date }));
    },
    [dispatch],
  );

  const onEndDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setUploadedDateFilterEndDateAction({ endDate: date }));
    },
    [dispatch],
  );

  const onRemoveFilter = (filter: SelectedFilter) => {
    dispatch(removeFilterAction({ filter }));
  };

  return (
    <FilterCard currentFilter="dateUploaded" onRemoveFilter={onRemoveFilter} {...props}>
      <UploadedDateFilter
        startDate={startDate}
        endDate={endDate}
        dateType={dateType}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onTypeChange={onDateTypeChange}
      />
    </FilterCard>
  );
});

UploadedDateFilterCard.displayName = 'UploadedDateFilterCard';
